import clsx from 'clsx';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import AboutUsSiteInnerContainer from 'components/about-us/AboutUsSiteInnerContainer';
import AboutUsSplitHero from 'components/about-us/AboutUsSplitHero';
import CarouselButtons from 'components/carousel2025/CarouselButtons';
import EmblaCarousel from 'components/carousel2025/EmblaCarousel';
import BulletBox from 'components/common/BulletBox';
import DashboardComponentHeader from 'components/dashboard/common/DashboardComponentHeader';
import MainLayout from 'components/layout/MainLayout';
import RoundedButton from 'components/RoundedButton';
import useEmblaCarousel from 'embla-carousel-react';
import { useIsMobile } from 'hooks/ui/useIsMobile';
import { NextSeo } from 'next-seo';
import Link from 'next/link';
import styles from './ResponsiblePlay.module.css';

const GotYourBack = (item: GotYourBack) => (
  <div className={styles.gotYourBack}>
    <div className={styles.gotYourBackIcon}>{item.icon}</div>
    <div className="aboutUsTypographyHeaderMedium">{item.title}</div>
    <div className={styles.gotYourBackInfo}>{item.info}</div>
  </div>
);

const spotTheSigns = [
  'Am I spending more than I planned?',
  'Is playing affecting my mood or stress levels?',
  'Am I keeping it from people close to me?',
];

const takeControl = [
  'Pause or cancel your membership anytime, no questions asked',
  'Set a reminder to review your spending monthly',
  'Take a break - our team can help you self-exclude if you need space',
];

interface GotYourBack {
  icon: string;
  title: string;
  info: string | JSX.Element;
}

const gotYourBack: GotYourBack[] = [
  {
    icon: '💷',
    title: '£120/month spend limit',
    info: "Hard capped, no exceptions. You can't overspend even if you wanted to.",
  },
  {
    icon: '⏱️',
    title: 'No instant win games',
    info: "We've avoided the rapid-fire format that's most linked to compulsive play.",
  },
  {
    icon: '🎁',
    title: 'No cash prizes',
    info: 'Our prizes are holidays, experiences and lifestyle items, not money. This keeps the focus on fun, not financial gain.',
  },
  {
    icon: '💳',
    title: 'No credit card payments',
    info: 'We only accept debit cards to ensure you have the means to fund your weekly ticket.',
  },
  {
    icon: '👁️',
    title: 'Transparency',
    info: (
      <>
        We are open about{' '}
        <Link href="/about-us">

          <strong>
            <u>who we are</u>
          </strong>

        </Link>
        , we publish the odds for each prize and have a London-based support
        team.
      </>
    ),
  },
];

const needToTalkToSomeone = [
  {
    title: 'GamCare',
    number: '0808 8020 133',
    link: 'gamcare.org.uk',
    image:
      'https://daymade-website-assets.s3.amazonaws.com/2026/responsible-play/GamCare.svg',
  },
  {
    title: 'BeGambleAware',
    link: 'begambleaware.org',
    image:
      'https://daymade-website-assets.s3.amazonaws.com/2026/responsible-play/BeGambleAware.svg',
  },
  {
    title: 'Citizens Advice',
    link: 'citizensadvice.org.uk',
    image:
      'https://daymade-website-assets.s3.amazonaws.com/2026/responsible-play/CitizensAdvice.svg',
  },
  {
    title: 'Money Advice',
    link: 'moneyadvicetrust.org',
    image:
      'https://daymade-website-assets.s3.amazonaws.com/2026/responsible-play/MoneyTrustAdvice.svg',
  },
  {
    title: 'National Debtline',
    link: 'nationaldebtline.org',
    image:
      'https://daymade-website-assets.s3.amazonaws.com/2026/responsible-play/NationalDebtline.svg',
  },
  {
    title: 'Mind',
    link: 'mind.org.uk',
    image:
      'https://daymade-website-assets.s3.amazonaws.com/2026/responsible-play/Mind.svg',
  },
  {
    title: 'Gambling Therapy',
    link: 'gamblingtherapy.org',
    image:
      'https://daymade-website-assets.s3.amazonaws.com/2026/responsible-play/GordonMoody.svg',
  },
];

const ResponsiblePlay = () => {
  const { isMobile } = useIsMobile();
  const emblaCarouselInfo = useEmblaCarousel({
    loop: false,
    duration: 10,
    inViewThreshold: 0.1,
  });

  return (
    <MainLayout limitItemWidths>
      <NextSeo
        title="Win Holidays Online | Responsible Play | Prize Platform | DAYMADE"
        description="Play with DAYMADE for your chance to win holidays online. Become a winner from the comfort of your home. Find out about us, our prizes, and what we do here."
        openGraph={{
          title:
            'Win Holidays Online | Responsible Play | Prize Platform | DAYMADE',
          description:
            'Play with DAYMADE for your chance to win holidays online. Become a winner from the comfort of your home. Find out about us, our prizes, and what we do here.',
        }}
      />
      <div className={styles.fixedHeaderHeightFudge}></div>
      <div className="daymadeTypographyReset">
        <AboutUsSplitHero
          imagePath={`https://daymade-website-assets.s3.amazonaws.com/2026/responsible-play/responsible-play-header.png`}
          imageAlt="Group of friends in a car"
          mobileHeroHeight="small"
          withReducedPadding
          extendBackgroundBelow
        >
          <h1 className="aboutUsTypographyHeaderXlargeOnTeal">
            Play responsibly
          </h1>
          <p>
            DAYMADE prides itself in being a fair platform which sets the
            standard on how to operate a prize draw that is designed to be
            entered safely and responsibly.
          </p>
          <p>
            At DAYMADE, fun is the whole point. But we also know that any form
            of play, however lighthearted, deserves a bit of mindfulness.
          </p>
          <p>Here's how to keep it that way.</p>
        </AboutUsSplitHero>

        <AboutUsModule background="grey">
          <AboutUsSiteContainer overlapAbove>
            <AboutUsSiteInnerContainer
              background="white"
              borderRadius
              withPadding
            >
              <DashboardComponentHeader
                loading={false}
                title={"💭 Know Why You're Playing"}
                separator
              />

              <p className={styles.blockText}>
                DAYMADE is about the thrill of possibility and discovering cool
                prizes that make your day. It's entertainment, not a financial
                strategy. If you ever catch yourself playing to "make back"
                money or relying on a win, that's worth pausing on.
              </p>
              <p className={styles.blockText}>
                <strong>Play for fun. Not for income.</strong>
              </p>
            </AboutUsSiteInnerContainer>
          </AboutUsSiteContainer>
        </AboutUsModule>

        <AboutUsModule background="grey">
          <AboutUsSiteContainer overlapBelow>
            <AboutUsSiteInnerContainer
              background="white"
              borderRadius
              withPadding
            >
              <DashboardComponentHeader
                loading={false}
                title={'⚠️ Spot the Signs'}
                separator
              />

              <p className={styles.blockText}>Ask yourself honestly:</p>
              <div className={styles.bulletBoxWrapper}>
                {spotTheSigns.map((item) => (
                  <BulletBox key={item} text={item} />
                ))}
              </div>
              <p className={styles.blockText}>
                <strong>
                  If you nodded at any of those, it might be time to take a step
                  back.
                </strong>
              </p>
            </AboutUsSiteInnerContainer>
          </AboutUsSiteContainer>
        </AboutUsModule>

        <AboutUsModule
          background="tealGradient"
          extendBackgroundAbove
          extendBackgroundBelow
        >
          <AboutUsSiteContainer>
            <h2
              className={clsx(
                styles.gotYourBackHeader,
                'aboutUsTypographyHeaderXlarge',
              )}
            >
              How we've got your back
            </h2>
            <p className="aboutUsTypographyCentered">
              We've deliberately built DAYMADE to be a lower-risk way to play:
            </p>
            <div className={styles.carouselContainer}>
              <EmblaCarousel
                className={styles.carousel}
                emblaCarouselInfo={emblaCarouselInfo}
                hidePips={isMobile ? false : true}
                items={gotYourBack.map((item) => (
                  <GotYourBack
                    key={item.title}
                    icon={item.icon}
                    title={item.title}
                    info={item.info}
                  />
                ))}
              >
                <CarouselButtons
                  changeSelectedIndex={(x) =>
                    x < 0
                      ? emblaCarouselInfo[1]?.scrollPrev()
                      : emblaCarouselInfo[1]?.scrollNext()
                  }
                />
              </EmblaCarousel>
            </div>
          </AboutUsSiteContainer>
        </AboutUsModule>

        <AboutUsModule background="grey">
          <AboutUsSiteContainer overlapAbove>
            <AboutUsSiteInnerContainer
              background="white"
              borderRadius
              withPadding
            >
              <DashboardComponentHeader
                loading={false}
                title={'💪 Take control'}
                separator
              />

              <p className={styles.blockText}>
                You're always in the driver's seat:
              </p>
              <div className={styles.bulletBoxWrapper}>
                {takeControl.map((item) => (
                  <BulletBox key={item} text={item} />
                ))}
              </div>
            </AboutUsSiteInnerContainer>
          </AboutUsSiteContainer>
        </AboutUsModule>

        <AboutUsModule background="grey">
          <AboutUsSiteContainer overlapBelow>
            <AboutUsSiteInnerContainer
              background="white"
              borderRadius
              withPadding
            >
              <DashboardComponentHeader
                loading={false}
                title={'📞 Need to talk to someone?'}
                separator
              />

              <p className={styles.blockText}>
                You're not alone. These are free, confidential and genuinely
                helpful:
              </p>
              <div className={styles.adviceItems}>
                {needToTalkToSomeone.map((item) => (
                  <div className={styles.adviceItem} key={item.title}>
                    <div className={styles.adviceImageWrapper}>
                      <div className={styles.adviceImage}>
                        <img src={item.image} alt={item.title} />
                      </div>
                    </div>
                    <div className={styles.adviceText}>
                      <p className={styles.adviceTextTitle}>
                        <strong>{item.title}</strong>
                      </p>
                      {item.number && (
                        <a
                          className={styles.adviceTextNumber}
                          rel="noopener noreferrer"
                          href={`tel:${item.number.replace(/\s+/g, '')}`}
                        >
                          {item.number}
                        </a>
                      )}
                      <a
                        className={styles.adviceTextLink}
                        target="_blank"
                        href={`https://www.${item.link}`}
                      >
                        {item.link}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </AboutUsSiteInnerContainer>
          </AboutUsSiteContainer>
        </AboutUsModule>

        <AboutUsModule background="tealGradient" extendBackgroundAbove>
          <AboutUsSiteContainer>
            <h2 className="aboutUsTypographyHeaderXlarge">
              Questions about your account or spend?
            </h2>
            <p className="aboutUsTypographyCentered">
              Drop us a line at{' '}
              <a href="mailto:hello@daymade.co.uk">hello@daymade.co.uk</a>.
              We're always happy to help.
            </p>
            <div className={styles.getInTouchWrapper}>
              <RoundedButton href="mailto:hello@daymade.co.uk" linkIsExternal>
                Get in Touch
              </RoundedButton>
            </div>
            <p className="aboutUsTypographyCentered">
              DAYMADE is a signatory of the Voluntary Code of Good Practice for
              Prize Draw Operators to help protect players. Read more in our{' '}
              <Link href="/blog/playing-fair-how-we-do-prize-draws">
                blog post
              </Link>
              .
            </p>
          </AboutUsSiteContainer>
        </AboutUsModule>
      </div>
    </MainLayout>
  );
};

export default ResponsiblePlay;
